/**
 * Local-only card pool (v1, localStorage). D1 sync runs in parallel (best-effort).
 *
 * Each user has one pool keyed by their email/id. Card statuses: pending / in_progress
 * / done / dropped. `age` increments per segment a card sits unselected.
 *
 * 2026-05-19: schema redesigned — (goal + strategy) merged into single `action` field.
 * 2026-05-20: Desk page calls fetchAllCards to seed the pool from D1 on mount.
 */
import type { CardStatus, DraftCard } from '../types';
import { apiGet } from '../api/client';

const STORAGE_PREFIX = 'gime5.cardPool.v1';

export interface PoolCard {
  id: string;
  title: string;
  action: string;
  actionHistory: string[];
  status: CardStatus;
  age: number;
  createdAt: number;
  updatedAt: number;
}

function key(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function loadPool(userId: string): PoolCard[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as PoolCard[];
  } catch {
    return [];
  }
}

export function savePool(userId: string, cards: PoolCard[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key(userId), JSON.stringify(cards));
}

/** Cards that should appear in the SelectCards screen alongside freshly drafted ones. */
export function activePoolCards(userId: string): PoolCard[] {
  return loadPool(userId).filter(
    (c) => c.status === 'pending' || c.status === 'in_progress',
  );
}

/** Completed cards — used by the archive collapsible in SelectCards. */
export function archivedCards(userId: string): PoolCard[] {
  return loadPool(userId)
    .filter((c) => c.status === 'done')
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Convert LLM draft cards into pool cards (status=pending, age=0). */
export function draftToPool(drafts: DraftCard[]): PoolCard[] {
  const now = Date.now();
  return drafts.map((d, i) => ({
    id: `c_${now}_${i}_${Math.random().toString(36).slice(2, 8)}`,
    title: d.title,
    action: d.action,
    actionHistory: [],
    status: 'pending' as CardStatus,
    age: 0,
    createdAt: now,
    updatedAt: now,
  }));
}

/** Persist newly drafted cards + unselected old ones (with age++); selected ones become in_progress. */
export function commitSelection(
  userId: string,
  newDrafts: PoolCard[],
  oldPool: PoolCard[],
  selectedIds: Set<string>,
): PoolCard[] {
  const all = [...newDrafts, ...oldPool];
  const merged = all.map((c) => {
    if (selectedIds.has(c.id)) {
      return { ...c, status: 'in_progress' as CardStatus, age: 0, updatedAt: Date.now() };
    }
    if (oldPool.some((o) => o.id === c.id)) {
      return { ...c, age: c.age + 1, updatedAt: Date.now() };
    }
    return c;
  });
  savePool(userId, merged);
  return merged;
}

export function updateCardStatus(
  userId: string,
  cardId: string,
  status: CardStatus,
  patch: Partial<Pick<PoolCard, 'action' | 'actionHistory'>> = {},
): void {
  const pool = loadPool(userId);
  const next = pool.map((c) =>
    c.id === cardId ? { ...c, ...patch, status, updatedAt: Date.now() } : c,
  );
  savePool(userId, next);
}

interface RemoteCardDTO {
  id: string;
  title: string;
  action: string;
  actionHistory: string[];
  status: CardStatus;
  age: number;
  originSegmentId: string | null;
  originWish: string | null;
  lastTraceAt: number | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * Pull the user's cards from D1 and merge into local pool.
 *
 * Strategy: D1 is the source of truth for cards it returns. Local-only cards
 * (e.g. cards that exist locally but the server doesn't know about, or cards
 * the server has dropped from its visible set like local dropped) are kept.
 * When ids overlap, D1 wins.
 */
export async function fetchAllCards(userId: string): Promise<PoolCard[]> {
  let remote: RemoteCardDTO[] = [];
  try {
    const res = await apiGet<{ ok: boolean; cards?: RemoteCardDTO[] }>('/api/cards');
    if (res.ok && Array.isArray(res.cards)) remote = res.cards;
  } catch (err) {
    console.warn('[cards/list] failed, falling back to local pool only', err);
    return loadPool(userId);
  }

  const local = loadPool(userId);
  const remoteIds = new Set(remote.map((r) => r.id));
  const localOnly = local.filter((c) => !remoteIds.has(c.id));
  const merged: PoolCard[] = [
    ...remote.map((r) => ({
      id: r.id,
      title: r.title,
      action: r.action,
      actionHistory: r.actionHistory ?? [],
      status: r.status,
      age: r.age,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    })),
    ...localOnly,
  ];
  savePool(userId, merged);
  return merged;
}

/** Apply a reframed action: push old to history, replace current. */
export function applyReframedAction(
  userId: string,
  cardId: string,
  newAction: string,
): void {
  const pool = loadPool(userId);
  const next = pool.map((c) => {
    if (c.id !== cardId) return c;
    return {
      ...c,
      actionHistory: [...c.actionHistory, c.action],
      action: newAction,
      updatedAt: Date.now(),
    };
  });
  savePool(userId, next);
}
